// FILE: src/Components/CreateGroup/CreateBrandWorkflowModal.js
import React, { useState } from 'react';
import { Modal, message, Input, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { createGroup } from '../../services/user.profile';
import UpgradeModal from '../BuySubscription/UpgradeModal';
import { fetchGroups, setSelectedGroup } from '../../store/Slices/GroupSlice';

export default function CreateBrandWorkflowModal({
  isOpen,
  onClose,
  onBrandCreateComplete,
}) {
  const [brandName, setBrandName] = useState('');
  const [website, setWebsite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({
    visible: false,
    message: '',
    featureName: '',
  });

  const dispatch = useDispatch();

  const resetForm = () => {
    setBrandName('');
    setWebsite('');
    setIsLoading(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleCreateBrand = async () => {
    if (!brandName.trim() || !website.trim()) {
      message.error('Brand Name and Website are required.');
      return;
    }

    if (!website.includes('.')) {
      message.error('Please enter a valid website URL.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await createGroup({
        groupName: brandName.trim(),
        website: website.trim(),
      });

      if (response && response.success && response.group?._id) {
        const createdBrandName = response.group.name || brandName.trim();

        await dispatch(fetchGroups());
        dispatch(setSelectedGroup(createdBrandName));

        message.success(
          'Brand created successfully. AI analysis is running in the background.'
        );

        resetForm();

        if (typeof onBrandCreateComplete === 'function') {
          onBrandCreateComplete(createdBrandName, 'background_processing');
        }

        onClose();
      } else {
        message.error(response?.error || 'Failed to create brand.');
      }
    } catch (error) {
      if (
        error.response?.data?.errorType === 'LIMIT_REACHED'
      ) {
        setUpgradeModal({
          visible: true,
          message: error.response.data.message,
          featureName: 'Brands',
        });
      } else {
        message.error(
          error.message || 'An error occurred during brand creation.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="Create a New Brand"
        open={isOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={handleCreateBrand}
          >
            Create Brand
          </Button>,
        ]}
        closable
        maskClosable={!isLoading}
        centered
        width={700}
      >
        <div className="p-4 space-y-4">
          <p className="text-gray-600">
            Enter your new brand&apos;s name and website. We&apos;ll create the
            brand immediately and analyze the website in the background.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Name
            </label>
            <Input
              placeholder="e.g., Oolook"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Brand Website
            </label>
            <Input
              placeholder="e.g., https://oolook.in"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              disabled={isLoading}
              onPressEnter={handleCreateBrand}
            />
          </div>
        </div>
      </Modal>

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() =>
          setUpgradeModal({ visible: false, message: '', featureName: '' })
        }
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />
    </>
  );
}